/**
 * Runs async tasks with a concurrency limit, returning PromiseSettledResult[]
 * in the same order as the input tasks array.
 *
 * Unlike Promise.allSettled (which starts all tasks immediately), this function
 * only starts a new task when a running slot becomes available.
 *
 * @param tasks  Array of lazy task factories — each is started only when a slot opens.
 * @param concurrency  Maximum number of tasks running at the same time.
 * @returns PromiseSettledResult<T>[] matching the order of the input tasks array.
 */
export function promisePool<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number
): Promise<PromiseSettledResult<T>[]> {
  if (tasks.length === 0) {
    return Promise.resolve([]);
  }

  return new Promise(resolve => {
    const results: PromiseSettledResult<T>[] = new Array(tasks.length);
    let nextIndex = 0;
    let completedCount = 0;

    function runNext(): void {
      if (nextIndex >= tasks.length) return;

      const taskIndex = nextIndex++;
      const task = tasks[taskIndex];

      task().then(
        value => {
          results[taskIndex] = { status: 'fulfilled', value };
        },
        reason => {
          results[taskIndex] = { status: 'rejected', reason };
        }
      ).then(() => {
        completedCount++;
        if (completedCount === tasks.length) {
          resolve(results);
        } else {
          runNext();
        }
      });
    }

    // Start initial batch up to concurrency limit
    const initialBatch = Math.min(concurrency, tasks.length);
    for (let i = 0; i < initialBatch; i++) {
      runNext();
    }
  });
}
